import type { ReactNode } from "react";
import type { ProjectSummary } from "./projectIndex";

interface ProjectArticleProps {
  project: ProjectSummary;
  children: ReactNode;
}

export function ProjectArticle({ project, children }: ProjectArticleProps) {
  return (
    <>
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stem-dark/50 via-stem-dark/30 to-stem-dark/70" />
        <div className="absolute inset-0 flex flex-col justify-end pb-10 md:pb-14">
          <div className="section-padding max-w-6xl mx-auto w-full">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium mb-4 md:mb-6 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Projects
            </button>
            <span className="inline-block max-w-full px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-wider mb-3 leading-snug">
              {project.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-2">
              {project.title}
            </h1>
            <p className="text-white/70 text-sm md:text-base">{project.date}</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-stem-cream">
        <article className="section-padding max-w-6xl mx-auto text-stem-charcoal">
          <div className="space-y-8 md:space-y-10 [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:text-stem-dark [&_h2]:tracking-tight [&_p]:text-base [&_p]:md:text-lg [&_p]:leading-relaxed [&_p]:text-stem-charcoal [&_ul]:space-y-3 [&_li]:text-base [&_li]:md:text-lg [&_li]:leading-relaxed [&_li]:text-stem-charcoal">
            {children}
          </div>
        </article>
      </section>
    </>
  );
}

export function ProjectInlineImage({
  src,
  alt,
  maxWidth,
  fit = "cover",
}: {
  src: string;
  alt: string;
  maxWidth?: string;
  fit?: "cover" | "contain";
}) {
  return (
    <figure
      className={`overflow-hidden rounded-2xl bg-white ${maxWidth ? maxWidth : ""} mx-auto`}
    >
      <img
        src={src}
        alt={alt}
        className={
          fit === "contain"
            ? "w-full h-auto object-contain"
            : "w-full aspect-[4/3] object-cover object-center"
        }
      />
    </figure>
  );
}

export function ProjectHighlightCards({
  challenge,
  result,
}: {
  challenge: ReactNode;
  result: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
      <div className="bg-white rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-stem-dark flex items-center justify-center">
            <i className="ri-error-warning-line text-white text-lg" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-stem-dark">
            The Challenge
          </h3>
        </div>
        <div className="text-stem-gray text-sm md:text-base leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-3 [&_li]:pl-1 [&_li::marker]:text-revelBlue">
          {challenge}
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-revelBlue flex items-center justify-center">
            <i className="ri-trophy-line text-white text-lg" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-stem-dark">
            The Result
          </h3>
        </div>
        <div className="text-stem-gray text-sm md:text-base leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-3 [&_li]:pl-1 [&_li::marker]:text-revelBlue">
          {result}
        </div>
      </div>
    </div>
  );
}

export function ProjectProcessTimeline({
  items,
}: {
  items: { step: string; detail: ReactNode }[];
}) {
  return (
    <div>
      <h2 className="mb-8">The Process</h2>
      <div className="relative">
        <div className="absolute left-5 top-0 bottom-0 w-px bg-stem-sand hidden md:block" />
        <div className="space-y-6">
          {items.map((item, idx) => (
            <div key={item.step} className="flex gap-4 md:gap-6">
              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-stem-dark flex items-center justify-center">
                <span className="text-white text-sm font-bold">{idx + 1}</span>
              </div>
              <div className="bg-white rounded-2xl p-5 md:p-6 flex-1">
                <h4 className="text-base md:text-lg font-bold text-stem-dark mb-1">
                  {item.step}
                </h4>
                <div className="text-stem-gray text-sm leading-relaxed">
                  {item.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProjectLearningGrid({ items }: { items: ReactNode[] }) {
  return (
    <div>
      <h2 className="mb-8">What I Learned</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="bg-white rounded-2xl p-5 flex items-start gap-3"
          >
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-revelBlue/10 flex items-center justify-center mt-0.5">
              <i className="ri-check-line text-revelBlue text-sm" />
            </div>
            <div className="text-stem-charcoal text-sm leading-relaxed">
              {item}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectToolPills({ tools }: { tools: string[] }) {
  return (
    <div>
      <h2 className="mb-8">Tools & Technologies</h2>
      <div className="flex flex-wrap gap-3">
        {tools.map((tool) => (
          <span
            key={tool}
            className="px-4 py-2 rounded-full bg-white text-stem-charcoal text-sm font-medium border border-stem-sand"
          >
            {tool}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ProjectGallery({
  images,
}: {
  images: { src: string; alt: string; fit?: "cover" | "contain" }[];
}) {
  return (
    <div>
      <h2 className="mb-8">Gallery</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {images.map((image) => (
          <div
            key={image.src}
            className={`rounded-2xl overflow-hidden ${
              image.fit === "contain" ? "aspect-[3/4] bg-white" : "aspect-[4/3]"
            }`}
          >
            <img
              src={image.src}
              alt={image.alt}
              className={`w-full h-full object-center ${
                image.fit === "contain" ? "object-contain" : "object-cover"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectNextSteps({
  items,
  title = "Next Improvements",
}: {
  items: { title: string; detail: ReactNode }[];
  title?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-full bg-stem-dark flex items-center justify-center">
          <i className="ri-arrow-up-line text-white text-lg" />
        </div>
        <h2>{title}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item, idx) => (
          <div key={item.title} className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-7 h-7 rounded-full bg-revelBlue/10 text-revelBlue text-xs font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <h3 className="text-base md:text-lg font-bold text-stem-dark">
                {item.title}
              </h3>
            </div>
            <div className="text-stem-gray text-sm leading-relaxed">
              {item.detail}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
